import { Component, inject, OnChanges, ChangeDetectionStrategy, signal, input, output } from '@angular/core';
import { GuruEntity } from '../../../model/guru-entity';
import { ModelService } from '../../../services/model-service';
import { UserStateService, ConfigurationSection } from '../../../services/user-state.service';
import { SelectionUtils } from '../../selection-utils';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { StringUtils } from '../../../utils/string-utils';
import { DimensionalData } from '../../../model/dimensional-data';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-air-handler-unit-mechanical-connections',
  imports: [MatIcon, FormsModule],
  templateUrl: './air-handler-unit-mechanical-connections.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './air-handler-unit-mechanical-connections.component.css',
})
export class AirHandlerUnitMechanicalConnectionsComponent implements OnChanges {
  private readonly modelService = inject(ModelService);
  private readonly userStateService = inject(UserStateService);
  private sectionKey = ConfigurationSection.MECHANICAL_CONNECTIONS;
  readonly invalidEntities = input.required<string[]>();
  readonly engineKey = input.required<string>();
  readonly selectionEntities = input.required<GuruEntity[]>();
  readonly imagePrefix = input.required<string>();
  readonly valueChanged = output<void>();
  handArrangementSelection!: GuruEntity;
  readonly handArrangementImagePath = signal<string>('');
  readonly dimensionalData = signal<DimensionalData | undefined>(undefined);
  isExpanded = true;

  constructor() {
    this.isExpanded = !this.userStateService.isConfigurationSectionCollapsed(this.sectionKey);
  }

  ngOnChanges(): void {
    this.handArrangementSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USHandArrangment');
    this.loadCharacteristicData().then(
      () => (this.isExpanded = !this.userStateService.isConfigurationSectionCollapsed(this.sectionKey)),
      () => this.handArrangementImagePath.set(''),
    );
  }

  private async loadCharacteristicData(): Promise<void> {
    await firstValueFrom<GuruEntity[]>(this.modelService.executeHierarchy(this.engineKey(), 'hierDOC'));
    const arrangementFileName: string = await firstValueFrom<string>(this.modelService.getCharacteristicValue(this.engineKey(), 'UnitHandArrangementImage', 'FileName'));
    if (StringUtils.isNotBlank(arrangementFileName)) {
      this.handArrangementImagePath.set(this.imagePrefix() + arrangementFileName);
    } else {
      this.handArrangementImagePath.set('');
    }
    let length: string = await firstValueFrom<string>(this.modelService.getCharacteristicValue(this.engineKey(), 'USDimensions', 'Length'));
    let width: string = await firstValueFrom<string>(this.modelService.getCharacteristicValue(this.engineKey(), 'USDimensions', 'Width'));
    let height: string = await firstValueFrom<string>(this.modelService.getCharacteristicValue(this.engineKey(), 'USDimensions', 'Height'));
    if (StringUtils.isBlank(length)) {
      length = 'Unknown';
    }
    if (StringUtils.isBlank(width)) {
      width = 'Unknown';
    }
    if (StringUtils.isBlank(height)) {
      height = 'Unknown';
    }
    const data: DimensionalData = { length: length, width: width, height: height };
    this.dimensionalData.set(data);
  }

  selectionChanged(entity: GuruEntity, newValue: string): void {
    this.modelService.setValue(entity.Name, this.engineKey(), newValue).subscribe({
      next: () => {
        this.valueChanged.emit();
        entity.Value = newValue;
      },
    });
  }

  toggleExpansion(): void {
    this.isExpanded = !this.isExpanded;
    this.userStateService.setCollapsedValue(this.sectionKey, this.isExpanded);
  }

  getExpansionIcon(): string {
    return this.isExpanded ? 'arrow_drop_up' : 'arrow_drop_down';
  }

  isInvalidSelection(entityName: string) {
    return this.invalidEntities() && this.invalidEntities().indexOf(entityName) >= 0;
  }
}
